import React, { useRef, useEffect, useState, useCallback } from "react";
import "./ruleta.css";

const DESCUENTOS = [5, 10, 15, 20, 25, 30, 40, 50];
const COLORS = [
  "#534AB7", "#1D9E75", "#D85A30", "#D4537E",
  "#378ADD", "#639922", "#BA7517", "#E24B4A",
];
const N = DESCUENTOS.length;

const RuletaPromo = ({ onDescuentoObtenido }) => {
  const canvasRef = useRef(null);
  const angleRef = useRef(0);
  const rafRef = useRef(null);

  const [spinning, setSpinning] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [intentos, setIntentos] = useState(0);
  const [puedeGirar, setPuedeGirar] = useState(true);
  const [overlayVisible, setOverlayVisible] = useState(true);
  const [mostrarAnimacion, setMostrarAnimacion] = useState(false);

  const drawWheel = useCallback((angle) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;
    const R = cx - 6;
    const slice = (2 * Math.PI) / N;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < N; i++) {
      const start = angle + i * slice;
      const end = start + slice;

      // Sector
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, R, start, end);
      ctx.closePath();
      ctx.fillStyle = COLORS[i];
      ctx.fill();
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // Texto centrado en el sector
      const mid = start + slice / 2;
      const tr = R * 0.67;
      const tx = cx + tr * Math.cos(mid);
      const ty = cy + tr * Math.sin(mid);

      ctx.save();
      ctx.translate(tx, ty);
      ctx.rotate(mid + Math.PI / 2);
      ctx.fillStyle = "#fff";
      ctx.font = "bold 18px 'Segoe UI', system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor = "rgba(0,0,0,0.4)";
      ctx.shadowBlur = 4;
      ctx.fillText(DESCUENTOS[i] + "%", 0, 0);
      ctx.restore();
    }

    // Centro
    ctx.beginPath();
    ctx.arc(cx, cy, 28, 0, 2 * Math.PI);
    ctx.fillStyle = "#111";
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 3;
    ctx.stroke();
  }, []);

  useEffect(() => {
    drawWheel(angleRef.current);
  }, [drawWheel]);

  // Restaurar desde localStorage
  useEffect(() => {
    const data = localStorage.getItem("ruletaPromo");
    if (data) {
      try {
        const parsed = JSON.parse(data);
        if (parsed && parsed.descuento) {
          setResultado(parsed.descuento);
          setIntentos(parsed.intentos || 1);
          setPuedeGirar(parsed.puedeGirar !== false);
          if (onDescuentoObtenido) onDescuentoObtenido(parsed.descuento);
        }
      } catch (e) {
        localStorage.removeItem("ruletaPromo");
      }
    }
  }, [onDescuentoObtenido]);

  const getWinner = (angle) => {
    const slice = (2 * Math.PI) / N;
    const needle = -Math.PI / 2;
    let a = ((needle - angle) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
    return Math.floor(a / slice) % N;
  };

  const girar = () => {
    if (spinning || !puedeGirar) return;

    setSpinning(true);
    setResultado(null);

    const targetIdx = Math.floor(Math.random() * N);
    const slice = (2 * Math.PI) / N;
    const needle = -Math.PI / 2;

    const jitter = (Math.random() - 0.5) * slice * 0.6;
    const targetAngle = needle - (targetIdx * slice + slice / 2 + jitter);
    const fullSpins = (6 + Math.floor(Math.random() * 4)) * 2 * Math.PI;
    const startAngle = angleRef.current;
    const delta = ((startAngle - targetAngle) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
    const endAngle = startAngle - (fullSpins + delta);

    const duration = 5000;
    let startTime = null;

    const ease = (t) => 1 - Math.pow(1 - t, 4);

    const frame = (ts) => {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const t = Math.min(elapsed / duration, 1);
      angleRef.current = startAngle + (endAngle - startAngle) * ease(t);
      drawWheel(angleRef.current);

      if (t < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        angleRef.current = endAngle;
        drawWheel(angleRef.current);

        const winner = getWinner(angleRef.current);
        const descuento = DESCUENTOS[winner];
        const nuevoIntento = intentos + 1;
        const permitirOtro = nuevoIntento < 2;

        setResultado(descuento);
        setIntentos(nuevoIntento);
        setSpinning(false);
        setPuedeGirar(permitirOtro);

        const data = {
          descuento,
          intentos: nuevoIntento,
          puedeGirar: permitirOtro,
          fecha: new Date().toISOString(),
        };
        localStorage.setItem("ruletaPromo", JSON.stringify(data));

        if (onDescuentoObtenido) onDescuentoObtenido(descuento);
      }
    };

    rafRef.current = requestAnimationFrame(frame);
  };

  const aplicarDescuento = () => {
    setMostrarAnimacion(true);
    setTimeout(() => {
      setMostrarAnimacion(false);
      setOverlayVisible(false);
    }, 2000);
  };

  const resetear = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    angleRef.current = 0;
    setResultado(null);
    setIntentos(0);
    setSpinning(false);
    setPuedeGirar(true);
    localStorage.removeItem("ruletaPromo");
    drawWheel(0);
  };

  const spinLabel = spinning ? "..." : intentos > 0 && puedeGirar ? "VOLVER A GIRAR" : "GIRAR";

  if (!overlayVisible) return null;

  return (
    <>
      {mostrarAnimacion && (
        <div className="descuento-animacion">
          🎉 ¡Descuento aplicado!
          <br />
          <span style={{ fontSize: "48px", color: "#ffd700" }}>
            {resultado}% OFF
          </span>
        </div>
      )}

      <div className="overlay">
        <div className="modal grande">
          {!resultado ? (
            <>
              <h2 className="titulo">🎯 Gira la ruleta y gana hasta 50% OFF</h2>
              {intentos > 0 && (
                <p className="subtitulo">Segundo intento — ¡La suerte está de tu lado!</p>
              )}

              <div className="ruleta-container">
                <div className="flecha">▼</div>

                <canvas
                  ref={canvasRef}
                  width={360}
                  height={360}
                  className="ruleta-canvas"
                />

                <button
                  className="btn-centro"
                  onClick={girar}
                  disabled={spinning || !puedeGirar}
                >
                  {spinLabel}
                </button>
              </div>
            </>
          ) : (
            <div className="resultado">
              <h2>🎉 ¡Felicidades!</h2>
              <p>Ganaste un descuento de</p>
              <h1>{resultado}%</h1>
              <p>Aplica en nuestros programas</p>

              <div className="botones-resultado">
                <button className="btn-cerrar" onClick={aplicarDescuento}>
                  Aplicar descuento y continuar con el formulario
                </button>
                {/*<button className="btn-secundario" onClick={resetear}>
                  🔄 Tirar de nuevo (Prueba)
                </button>*/}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RuletaPromo;